import axiosInstance from "./axios";

// Comment endpoints for a comment target — a Post's comments (backend:
// blorp/views/comments.py, CommentViewSet) or an EventOccurrence's
// (blorp/views/event_engagement.py, EventOccurrenceCommentViewSet). A target is
// { type: "post" | "occurrence", id }. The two kinds live in separate tables,
// so the same comment ID can exist in both; every call names its target.
// Auth comes from the shared axios interceptor. Errors are rethrown as-is so
// callers can branch on err.response?.status.
//
// List endpoints are limit/offset paginated: { count, next, previous, results },
// where `next` is a relative /api/... path that can be requested directly.
// Root and reply lists are both ordered newest first.

export const POST_TARGET = "post";
export const OCCURRENCE_TARGET = "occurrence";

const base = (target) =>
  target.type === OCCURRENCE_TARGET
    ? `/event-occurrences/${target.id}/comments/`
    : `/posts/${target.id}/comments/`;

export const fetchRootComments = async (target) => {
  const { data } = await axiosInstance.get(base(target));
  return data;
};

export const fetchReplies = async (target, commentId) => {
  const { data } = await axiosInstance.get(`${base(target)}${commentId}/replies/`);
  return data;
};

// Follows a `next` link from a previous page.
export const fetchCommentPage = async (nextUrl) => {
  const { data } = await axiosInstance.get(nextUrl);
  return data;
};

export const fetchComment = async (target, commentId) => {
  const { data } = await axiosInstance.get(`${base(target)}${commentId}/`);
  return data;
};

// → { ancestors: [up to 2, oldest first], focus }
export const fetchCommentContext = async (target, commentId) => {
  const { data } = await axiosInstance.get(`${base(target)}${commentId}/context/`);
  return data;
};

// → { comment, comment_count }
export const createComment = async (target, text, parentId = null) => {
  const payload = { text };
  if (parentId != null) payload.parent = parentId;
  const { data } = await axiosInstance.post(base(target), payload);
  return data;
};

// → the updated comment
export const editComment = async (target, commentId, text) => {
  const { data } = await axiosInstance.patch(`${base(target)}${commentId}/`, { text });
  return data;
};

// Soft delete. → { detail, comment_count } (comment_count is absent when the
// comment was already deleted).
export const deleteComment = async (target, commentId) => {
  const { data } = await axiosInstance.delete(`${base(target)}${commentId}/`);
  return data;
};

// Sets the viewer's like to `liked`. Posts only toggle (POST toggle_like/);
// occurrence comments take an explicit POST (like) / DELETE (unlike), so an
// unlike can never turn into a like — and stays allowed after a cancellation.
// → { is_liked, likes_count }
export const setCommentLike = async (target, commentId, liked) => {
  if (target.type === OCCURRENCE_TARGET) {
    const url = `${base(target)}${commentId}/like/`;
    const { data } = liked ? await axiosInstance.post(url, {}) : await axiosInstance.delete(url);
    return data;
  }
  const { data } = await axiosInstance.post(`${base(target)}${commentId}/toggle_like/`, {});
  return data;
};

// Same type again removes it; a different type switches it. `isRemoval` (the
// viewer's current reaction picked again) is sent to occurrence comments as a
// DELETE, which a canceled occurrence still accepts; Posts toggle on POST.
// → { my_reaction, reaction_counts }
export const reactToComment = async (target, commentId, reactionType, { isRemoval = false } = {}) => {
  const url = `${base(target)}${commentId}/reaction/`;
  if (target.type === OCCURRENCE_TARGET && isRemoval) {
    const { data } = await axiosInstance.delete(url);
    return data;
  }
  const { data } = await axiosInstance.post(url, { reaction_type: reactionType });
  return data;
};
